import { Button } from '@/shared/components/ui/button';
import { QuestionRow, type RowState } from './V2QuestionRow';
import { useFocusedList } from '../hooks/useFocusedList';
import { personLabelFor, questionFor } from '../api/v2TakeApi';
import type {
  DiscoveryAnswers,
  V2Response,
  V2ResponseOption,
  V2TakeNeed,
  V2TakePillar,
} from '../api/v2TakeApi';

const GREEN = 'bg-[#026857] hover:bg-[#025247]';

/**
 * One pillar of the MVP 2 readiness questions (Feature 110), as design option
 * 2c "Focused list".
 *
 * Five questions, four options. Unlike V1's three-point scale this one carries
 * an explicit "Unsure": it scores the same as Not Yet, because the household
 * cannot demonstrate readiness either way, but it changes what the report says
 * — the first step becomes confirming what is already in place. So it is
 * offered as an honest answer rather than something to avoid.
 *
 * The page still holds the whole pillar, which keeps the save-and-continue
 * rhythm, but only the question being answered is expanded. Showing all five
 * with all four descriptions put twenty rows of reading on one screen.
 *
 * All five rows stay mounted and open or close in place. An earlier build
 * swapped between three separate row components, which meant the open card
 * appeared wherever the last one had been, with no motion and no fixed marker
 * — answering felt like the text changing rather than like progress. Three
 * things carry that now: the answer is left visible for a beat before the row
 * closes, the open/close is animated, and every row keeps a numbered chip whose
 * fill is the one thing that moves down the list.
 */

type V2PillarScreenProps = {
  pillar: V2TakePillar;
  index: number; // 0-based
  total: number;
  responseOptions: V2ResponseOption[];
  answers: Record<string, V2Response>;
  /** Discovery answers so far — D1 decides whose life each question is about. */
  discovery: DiscoveryAnswers;
  personLabels: Record<string, string>;
  personOwnLabels: Record<string, string>;
  /** The supporter's typed first name; replaces the relationship when set. */
  subjectName?: string;
  onAnswer: (needId: string, response: V2Response) => void;
  onBack: () => void;
  onContinue: () => void;
  isSaving?: boolean;
  saveError?: boolean;
};

export function V2PillarScreen({
  pillar,
  index,
  total,
  responseOptions,
  answers,
  discovery,
  personLabels,
  personOwnLabels,
  subjectName,
  onAnswer,
  onBack,
  onContinue,
  isSaving = false,
  saveError = false,
}: V2PillarScreenProps) {
  const needs = pillar.needs;
  const answeredCount = needs.filter((need) => answers[need.id] !== undefined).length;
  const allAnswered = answeredCount === needs.length;

  /** First question with no answer, or null when the pillar is complete. */
  const firstUnanswered = (given: Record<string, V2Response>): number | null => {
    const at = needs.findIndex((need) => given[need.id] === undefined);
    return at === -1 ? null : at;
  };

  const { activeIndex, rowRef, continueRef, reopen, advanceTo } = useFocusedList({
    firstUnanswered: () => firstUnanswered(answers),
    resetKey: pillar.key,
  });

  const select = (position: number, need: V2TakeNeed, value: V2Response) => {
    onAnswer(need.id, value);
    // Computed here rather than inside the timer: by the time it fires the
    // parent may not have re-rendered with this answer yet, and the closure
    // would reopen the question that was just answered.
    advanceTo(position, firstUnanswered({ ...answers, [need.id]: value }));
  };

  // Said once per pillar rather than hedging every question (Christine #2/#3).
  const person = personLabelFor(discovery, personLabels, subjectName);

  const labelOf = (value: V2Response): string =>
    responseOptions.find((option) => option.value === value)?.label ?? value;

  return (
    <div className="mx-auto w-full max-w-2xl">
      <div
        className="mb-6 flex items-center justify-center gap-2"
        aria-label={`Pillar ${index + 1} of ${total}`}
      >
        {Array.from({ length: total }).map((_, i) => (
          <span
            key={i}
            className={`h-2 rounded-full transition-all motion-reduce:transition-none ${
              i === index ? 'w-6 bg-[#026857]' : 'w-2 bg-gray-300'
            }`}
          />
        ))}
      </div>

      <p className="text-center text-xs font-semibold uppercase tracking-wide text-[#026857]">
        Pillar {pillar.num} of {total}
      </p>
      <h2 className="mt-1 text-center font-serif text-2xl text-gray-900">{pillar.name}</h2>
      {person !== undefined && (
        <p className="mt-2 text-center text-sm text-gray-600">
          Answer based on what you know about {person}. If you&apos;re not sure, choose Unsure.
        </p>
      )}

      <div className="mt-8 space-y-2">
        {needs.map((need, i) => {
          const answer = answers[need.id];
          const state: RowState =
            i === activeIndex ? 'open' : answer !== undefined ? 'answered' : 'todo';

          return (
            <QuestionRow
              key={need.id}
              question={questionFor(need, discovery, personLabels, personOwnLabels, subjectName)}
              position={i + 1}
              total={needs.length}
              state={state}
              responseOptions={responseOptions}
              answer={answer}
              answerLabel={answer !== undefined ? labelOf(answer) : undefined}
              isSaving={isSaving}
              onSelect={(value) => select(i, need, value)}
              onChange={() => reopen(i)}
              rowRef={state === 'open' ? rowRef : undefined}
            />
          );
        })}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <p className="text-[13px] text-gray-400">
          {answeredCount} of {needs.length} answered
        </p>
        <div className="flex items-center gap-2">
          {/* Disabled while the step is in flight: the save advances on success,
              so going back mid-request lands the client on the next screen
              anyway — their navigation silently loses to a response. */}
          <Button type="button" variant="ghost" disabled={isSaving} onClick={onBack}>
            Back
          </Button>
          <Button
            type="button"
            className={GREEN}
            disabled={!allAnswered || isSaving}
            onClick={onContinue}
            ref={continueRef}
          >
            {isSaving ? 'Saving…' : 'Continue'}
          </Button>
        </div>
      </div>
      {saveError && (
        <p className="mt-2 text-center text-sm text-red-600">
          Couldn&apos;t save your answers. Please try again.
        </p>
      )}
    </div>
  );
}
